import { useState, useEffect, createContext, useRef, useContext } from "react";
import { Conversation, Message } from "@twilio/conversations";
import { useParams } from "react-router-dom";
import { supabase } from "index";
import { ChatContext } from "./chat";

const MessagesContext = createContext<any | null>(null);

const MessagesConsumer = MessagesContext.Consumer;

function MessagesProvider({
  children,
  user,
}: {
  children: JSX.Element;
  user: any;
}) {
  const { id } = useParams();
  const { chatClient, initialized, online } = useContext(ChatContext);
  const [twilioConversation, setTwilioConversation] = useState<Conversation>();
  const [messages, setMessages] = useState<{
    [key: string]: Message[];
  }>({});

  const messagesRef = useRef<{
    [key: string]: Message[];
  }>(messages);

  const messageAdded = (message: Message) => {
    messagesRef.current = {
      ...messagesRef.current,
      [message.conversation.sid]: [
        ...(messagesRef.current[message.conversation.sid] || []),
        message,
      ],
    };
    setMessages(messagesRef.current);
  };

  useEffect(() => {
    if (chatClient?.listenerCount("messageAdded") === 0) {
      chatClient.on("messageAdded", messageAdded);
    }
    return () => {
      chatClient?.removeListener("messageAdded", messageAdded);
    };
  }, [chatClient]);

  const send = async (body: string, recipient: string) => {
    await twilioConversation?.sendMessage(body, {
      online: online[recipient],
    });
  };

  useEffect(() => {
    async function loadConversation() {
      try {
        const response = (await chatClient?.getConversationBySid(
          id as string,
        )) as Conversation;

        const { data: unreadNotifications } = await supabase
          .from("notifications")
          .select(`id, event!inner(conversation)`)
          .filter("event.conversation", "eq", id)
          .eq("dismissed", false)
          .eq("participant", user.id)
          .order("created_at", { ascending: true });

        if (unreadNotifications) {
          for (const notification of unreadNotifications) {
            await supabase
              .from("notifications")
              .update({ dismissed: true })
              .match({ id: notification.id });
          }
        }

        setTwilioConversation(response);

        const messagesPaginator = await response?.getMessages();
        const items = messagesPaginator?.items || [];

        messagesRef.current = {
          ...messagesRef.current,
          [id as string]: items,
        };
        setMessages(messagesRef.current);
      } catch (error) {
        console.error(error);
      }
    }

    if (id && initialized) {
      loadConversation();
    }
  }, [chatClient, id, initialized, user.id]);

  return (
    <MessagesContext.Provider
      value={{
        messages,
        send,
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
}
export { MessagesContext, MessagesConsumer, MessagesProvider };
